import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Chip, CircularProgress, Container, Snackbar } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { RootState, AppDispatch } from '../../Redux/Store';
import { fetchMenuPreview } from '../../Redux/MenuPreview/menuPreviewApi';
import { createApiRequest } from '../../utils/apiUtils';
import PublicHeader from '../../Components/Header/PublicHeader';
import CategoryTabs from '../../Components/Order/CategoryTabs';
import { MenuList } from '../../Components/Order/MenuList';
import type { OrderCategory } from '../../types/order';

const CLOUDFRONT_URL = process.env.REACT_APP_CLOUDFRONT_URL;

/**
 * メニュープレビュー。お客様に見える公開注文画面（Public/Order）と同じコンポーネント
 * （PublicHeader / CategoryTabs / MenuList）で表示する（feast-fe ADR 0030）。
 * カート・店員呼び出し・数量モーダルはプレビューでは動作させない。
 */
const MenuPreview: React.FC = () => {
  const { store_id } = useParams<{ store_id: string }>();
  const dispatch: AppDispatch = useDispatch();
  const { categories, loading, error } = useSelector((state: RootState) => state.menuPreview);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showPreviewNote, setShowPreviewNote] = useState(false);
  // オーダー画面テンプレート（店舗設定）。実際の注文画面と同じ見た目にするため反映する。
  const [orderTemplate, setOrderTemplate] = useState<'IMAGE' | 'TEXT'>('IMAGE');

  useEffect(() => {
    if (store_id) {
      dispatch(fetchMenuPreview(store_id));
      createApiRequest<{ template: 'IMAGE' | 'TEXT' }>(`/stores/${store_id}/order-menu-template`, {
        method: 'GET',
        requiresAuth: true,
      })
        .then((data) => setOrderTemplate(data.template === 'TEXT' ? 'TEXT' : 'IMAGE'))
        .catch(() => undefined);
    }
  }, [dispatch, store_id]);

  useEffect(() => {
    if (categories.length > 0) {
      setSelectedCategory(categories[0].id);
    }
  }, [categories]);

  // 管理側の MenuCategory/MenuItem を公開注文側の OrderCategory/OrderItem 形へ変換する。
  // menuOptionId は注文確定時にのみ意味を持つためプレビューでは null 固定。
  const previewCategories: OrderCategory[] = useMemo(
    () =>
      categories.map((category) => ({
        id: category.id,
        name: category.name,
        sortIndex: category.sortIndex,
        items: (category.items || []).map((menu) => ({
          id: menu.id,
          name: menu.name,
          price: menu.price,
          imageUrl: menu.imageUrl || '',
          menuOptionGroupId: menu.menuOptionGroupId,
          menuId: menu.id,
          menuOptionId: null,
          isActive: menu.isActive,
          sortIndex: menu.sortIndex,
          isSuspended: menu.isSuspended ?? false,
        })),
      })),
    [categories],
  );

  const selectedCategoryItems =
    previewCategories.find((category) => category.id === selectedCategory)?.items || [];

  return (
    <Box>
      <PublicHeader storeName="メニュープレビュー" />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Chip
          icon={<VisibilityOutlinedIcon />}
          label="プレビュー表示 — お客様の注文画面と同じ見た目です（注文はできません）"
          color="primary"
          variant="outlined"
          sx={{ mb: 2 }}
        />
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            <CategoryTabs
              categories={previewCategories}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
            />
            <MenuList
              items={selectedCategoryItems}
              onAddClick={() => setShowPreviewNote(true)}
              cloudFrontUrl={CLOUDFRONT_URL || ''}
              template={orderTemplate}
            />
          </>
        )}
      </Container>

      <Snackbar
        open={showPreviewNote}
        autoHideDuration={2500}
        onClose={() => setShowPreviewNote(false)}
        message="プレビューのため注文はできません"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default MenuPreview;
